/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import AbsenceModal from "./AbsenceModal";
import { analyseDecisionNote } from "../../utils/absenceDecisionIntelligence";
import EvidencePreviewModal from "./EvidencePreviewModal";
import SensitiveActionModal from "../security/SensitiveActionModal";
import {
  hasRecentSensitiveActionVerification,
} from "../../utils/sensitiveActionAuth";
import { getSecurityLockdown } from "../../utils/securityLockdown";


/* =========================
   HELPERS
========================= */


/* =========================
   PRESENTPLUS BRANDING
   Uses the school colour from AdminManager CSS vars.
   Falls back safely if the old shell has not been updated yet.
========================= */
const BRAND = "var(--pp-brand-primary, var(--pp-primary, #4f46e5))";
const BRAND_DARK = "var(--pp-brand-dark, var(--pp-primary-dark, #3730a3))";
const BRAND_SOFT = "var(--pp-brand-soft, var(--pp-primary-soft, rgba(79,70,229,0.10)))";
const BRAND_BORDER = "var(--pp-brand-border, var(--pp-primary-border, rgba(79,70,229,0.22)))";
const BRAND_SHADOW = "var(--pp-brand-shadow, var(--pp-primary-shadow, rgba(79,70,229,0.24)))";
const BRAND_GRADIENT = "linear-gradient(135deg, var(--pp-brand-primary, var(--pp-primary, #2563eb)), var(--pp-brand-dark, var(--pp-primary-dark, #4f46e5)))";

function toDate(value) {
  if (!value) return null;
  try {
    if (value?.toDate) return value.toDate();
    if (value?.seconds) return new Date(value.seconds * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function formatDate(value) {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getAbsenceDateLabel(report) {
  if (report?.dateRangeLabel) return report.dateRangeLabel;

  const start = report?.startDate || report?.date || report?.dateKey || report?.createdAt;
  const end = report?.endDate;

  if (start && end && String(start) !== String(end)) {
    return `${formatDate(start)} - ${formatDate(end)}`;
  }

  return formatDate(start);
}

function getAbsenceDurationLabel(report) {
  const days = Number(report?.durationDays || 1);
  if (days > 1) return `${days} days`;
  return niceText(report?.duration || "single day");
}

function formatDateTime(value) {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getSubmittedTime(report) {
  return (
    toDate(report?.createdAt)?.getTime() ||
    toDate(report?.submittedAt)?.getTime() ||
    toDate(report?.proofUploadedAt)?.getTime() ||
    toDate(report?.updatedAt)?.getTime() ||
    toDate(report?.date || report?.dateKey)?.getTime() ||
    0
  );
}

function normalizeStatus(value) {
  return String(value || "submitted").toLowerCase().trim();
}

function statusLabel(value) {
  const status = normalizeStatus(value);
  if (status === "approved" || status === "resolved") return "Approved";
  if (status === "rejected") return "Rejected";
  if (status === "reviewing") return "Under review";
  return "Submitted";
}

function niceText(value) {
  if (!value) return "—";
  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getProofPath(report) {
  return (
    report?.proofPath ||
    report?.filePath ||
    report?.storagePath ||
    report?.evidencePath ||
    report?.proof?.path ||
    report?.evidence?.path ||
    report?.attachment?.path ||
    ""
  );
}

function hasProof(report) {
  return !!getProofPath(report);
}

function getStudentName(report, studentMap) {
  return (
    report?.studentName ||
    report?.childName ||
    studentMap?.[report?.studentId]?.name ||
    studentMap?.[report?.studentId]?.fullName ||
    studentMap?.[report?.studentId]?.displayName ||
    report?.studentId ||
    "Student"
  );
}

function getParentName(report) {
  return (
    report?.parentName ||
    report?.submittedByName ||
    report?.name ||
    report?.parentEmail ||
    report?.parentId ||
    "Parent"
  );
}

function getExplanation(report) {
  return (
    report?.notes ||
    report?.note ||
    report?.message ||
    report?.explanation ||
    report?.description ||
    ""
  );
}

function addHours(hours) {
  return Timestamp.fromDate(
    new Date(Date.now() + Number(hours || 72) * 60 * 60 * 1000)
  );
}

function isOpenStatus(status) {
  const value = normalizeStatus(status);
  return value === "submitted" || value === "reviewing" || value === "";
}

function getCountdown(report) {
  const deleteStatus = String(report?.proofDeleteStatus || "").toLowerCase();

  if (report?.proofDeletedAt || deleteStatus === "deleted") {
    return report?.proofDeletedAt
      ? `Evidence deleted ${formatDateTime(report.proofDeletedAt)}`
      : "Evidence deleted";
  }

  if (deleteStatus === "deleting") return "Automatic deletion in progress";
  if (deleteStatus === "delete_failed") return "Automatic deletion retry pending";
  if (!hasProof(report)) return "No evidence";

  const deleteAfter = toDate(report?.proofDeleteAfter);

  if (!deleteAfter) {
    const status = normalizeStatus(report?.status);
    if (status === "approved" || status === "rejected" || status === "resolved") {
      return "Deletion pending";
    }
    return "Deletes after decision";
  }

  const diff = deleteAfter.getTime() - Date.now();
  if (diff <= 0) return "Due for deletion";

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return `${days}d ${remHours}h left`;
  }

  return `${hours}h ${mins}m left`;
}

function textIncludesAny(text, words = []) {
  const hay = String(text || "").toLowerCase();
  return words.some((word) => hay.includes(word));
}

function isSameDay(value) {
  const d = toDate(value);
  if (!d) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function calculatePriority(report, allReports = []) {
  let score = 0;
  const flags = [];
  const explanation = getExplanation(report);
  const combinedText = `${report?.reason || ""} ${explanation}`.toLowerCase();

  if (hasProof(report)) {
    score += 30;
    flags.push("Evidence attached");
  }

  if (
    textIncludesAny(combinedText, [
      "hospital",
      "medical",
      "doctor",
      "gp",
      "dentist",
      "dental",
      "clinic",
      "appointment",
      "emergency",
    ])
  ) {
    score += 25;
    flags.push("Medical / appointment");
  }

  if (isSameDay(report?.date || report?.dateKey || report?.createdAt)) {
    score += 20;
    flags.push("Same-day report");
  }

  if (String(explanation || "").trim().length > 140) {
    score += 15;
    flags.push("Detailed note");
  }

  if (
    textIncludesAny(combinedText, [
      "urgent",
      "accident",
      "ambulance",
      "serious",
      "operation",
      "unable",
    ])
  ) {
    score += 10;
    flags.push("Urgent wording");
  }

  const studentId = String(report?.studentId || "");
  const recentCutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;

  const recentSameStudent = allReports.filter((item) => {
    if (String(item?.studentId || "") !== studentId) return false;
    const d = toDate(item?.createdAt || item?.date || item?.dateKey);
    if (!d) return false;
    return d.getTime() >= recentCutoff;
  });

  if (studentId && recentSameStudent.length >= 3) {
    score += 15;
    flags.push("Repeated recent reports");
  }

  const finalScore = Math.max(0, Math.min(100, score));

  if (finalScore >= 70) {
    return { score: finalScore, level: "High priority", tone: "high", flags };
  }

  if (finalScore >= 40) {
    return { score: finalScore, level: "Medium priority", tone: "medium", flags };
  }

  return { score: finalScore, level: "Normal", tone: "low", flags };
}

function Stat({ label, value }) {
  return (
    <div style={styles.stat}>
      <div style={styles.statValue}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  );
}

function PriorityBadge({ priority }) {
  const tone =
    priority?.tone === "high"
      ? styles.priorityHigh
      : priority?.tone === "medium"
        ? styles.priorityMedium
        : styles.priorityLow;

  return (
    <span style={{ ...styles.priorityBadge, ...tone }}>
      {priority?.level || "Normal"}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = normalizeStatus(status);
  const tone =
    s === "approved" || s === "resolved"
      ? styles.statusApproved
      : s === "rejected"
        ? styles.statusRejected
        : s === "reviewing"
          ? styles.statusReviewing
          : styles.statusSubmitted;

  return (
    <span style={{ ...styles.statusBadge, ...tone }}>
      {statusLabel(status)}
    </span>
  );
}

/* =========================
   MAIN COMPONENT
========================= */

export default function AbsenceSection({ schoolId, adminUid, focusReportId = "" }) {
  const currentSchoolId = schoolId || "school_001";

  const [reports, setReports] = useState([]);
  const [students, setStudents] = useState([]);
  const [settings, setSettings] = useState({
    evidenceRetentionHoursAfterDecision: 72,
    evidenceHardDeleteDays: 7,
  });

  const [statusFilter, setStatusFilter] = useState("open");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [autoOpenNext, setAutoOpenNext] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
const [evidenceUrl, setEvidenceUrl] = useState("");
const [evidenceFileName, setEvidenceFileName] = useState("");
const [evidenceLoading, setEvidenceLoading] = useState(false);
const [evidenceError, setEvidenceError] = useState("");
const [pendingEvidence, setPendingEvidence] = useState(null);
const [showSensitiveModal, setShowSensitiveModal] = useState(false);

  async function loadData() {
    try {
      const [reportSnap, studentSnap, settingsSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, "schools", currentSchoolId, "absenceReports"),
            orderBy("createdAt", "desc")
          )
        ),
        getDocs(collection(db, "schools", currentSchoolId, "students")),
        getDocs(collection(db, "schools", currentSchoolId, "settings")),
      ]);

      const reportList = reportSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const studentList = studentSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const absenceDoc = settingsSnap.docs.find((d) => d.id === "absence");

      if (absenceDoc) {
        const data = absenceDoc.data() || {};
        setSettings({
          evidenceRetentionHoursAfterDecision: Number(
            data.evidenceRetentionHoursAfterDecision || 72
          ),
          evidenceHardDeleteDays: Number(data.evidenceHardDeleteDays || 7),
        });
      }

      setReports(reportList);
      setStudents(studentList);
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to load absence reports.");
    }
  }

  useEffect(() => {
    loadData();

    // Refresh the queue automatically so completed backend deletions
    // appear without requiring the administrator to reload the page.
    const refreshTimer = window.setInterval(() => {
      loadData();
    }, 60 * 1000);

    return () => window.clearInterval(refreshTimer);
  }, [currentSchoolId]);

  const studentMap = useMemo(() => {
    const map = {};
    students.forEach((student) => {
      map[student.id] = student;
    });
    return map;
  }, [students]);

  const enrichedReports = useMemo(() => {
    return reports
      .map((report) => ({
        ...report,
        priority: calculatePriority(report, reports),
      }))
      .sort((a, b) => getSubmittedTime(b) - getSubmittedTime(a));
  }, [reports]);

const stats = useMemo(() => {
  return enrichedReports.reduce(
    (acc, r) => {
      const s = normalizeStatus(r.status);

      if (isOpenStatus(s)) acc.open += 1;
      if (s === "approved" || s === "resolved") acc.approved += 1;
      if (s === "rejected") acc.rejected += 1;
      if (hasProof(r)) acc.evidence += 1;

      if (isOpenStatus(s) && (r.priority?.score || 0) >= 70) {
        acc.highPriority += 1;
      }

      return acc;
    },
    {
      open: 0,
      approved: 0,
      rejected: 0,
      evidence: 0,
      highPriority: 0,
    }
  );
}, [enrichedReports]);

  const filteredReports = useMemo(() => {
    const q = search.trim().toLowerCase();

    return enrichedReports.filter((report) => {
      const status = normalizeStatus(report.status);

      if (statusFilter === "open" && !isOpenStatus(status)) return false;
      if (statusFilter === "priority" && (report.priority?.score || 0) < 70) return false;
      if (statusFilter === "approved" && !(status === "approved" || status === "resolved")) return false;
      if (statusFilter === "rejected" && status !== "rejected") return false;
      if (statusFilter === "submitted" && status !== "submitted") return false;
      if (statusFilter === "reviewing" && status !== "reviewing") return false;
      if (statusFilter === "evidence" && !hasProof(report)) return false;

      if (!q) return true;

      const hay = [
        getStudentName(report, studentMap),
        getParentName(report),
        report.reason,
        report.notes,
        report.note,
        report.parentEmail,
        report.studentId,
        report.dateRangeLabel,
        report.startDate,
        report.endDate,
        report.proofName,
        report.priority?.level,
        ...(report.priority?.flags || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [enrichedReports, statusFilter, search, studentMap]);

  const queueReports = useMemo(
    () => filteredReports.filter((report) => isOpenStatus(report.status)),
    [filteredReports]
  );

  useEffect(() => {
    if (!focusReportId || !reports.length) return;

    const target = reports.find((report) => {
      return (
        String(report.id || "") === String(focusReportId) ||
        String(report.reportId || "") === String(focusReportId) ||
        String(report.absenceId || "") === String(focusReportId)
      );
    });

    if (!target) return;

    setStatusFilter("all");

    const enrichedTarget = {
      ...target,
      priority: calculatePriority(target, reports),
    };

    const timer = setTimeout(() => {
      setSelectedReport(enrichedTarget);

      const row = document.getElementById(`absence-report-row-${target.id}`);
      row?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 180);

    return () => clearTimeout(timer);
  }, [focusReportId, reports]);

  useEffect(() => {
    function handleOpenAbsenceReport(event) {
      const absenceId =
        event?.detail?.absenceId ||
        event?.detail?.reportId ||
        event?.detail?.id ||
        "";

      if (!absenceId) return;

      const target = reports.find((report) => {
        return (
          String(report.id || "") === String(absenceId) ||
          String(report.reportId || "") === String(absenceId) ||
          String(report.absenceId || "") === String(absenceId)
        );
      });

      if (!target) return;

      setStatusFilter("all");
      setSelectedReport({
        ...target,
        priority: calculatePriority(target, reports),
      });

      setTimeout(() => {
        const row = document.getElementById(`absence-report-row-${target.id}`);
        row?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 180);
    }

    window.addEventListener("presentplus:openAbsenceReport", handleOpenAbsenceReport);

    return () => {
      window.removeEventListener("presentplus:openAbsenceReport", handleOpenAbsenceReport);
    };
  }, [reports]);

  const selectedReports = useMemo(
    () => enrichedReports.filter((report) => selectedIds.includes(report.id)),
    [selectedIds, enrichedReports]
  );

  const allVisibleSelected =
    filteredReports.length > 0 &&
    filteredReports.every((report) => selectedIds.includes(report.id));

  function getNextReport(currentId) {
    const index = queueReports.findIndex((report) => report.id === currentId);
    if (index === -1) return queueReports[0] || null;
    return queueReports[index + 1] || queueReports[index - 1] || null;
  }

  function toggleSelect(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }

  function toggleSelectAllVisible() {
    if (allVisibleSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !filteredReports.some((report) => report.id === id))
      );
      return;
    }

    setSelectedIds((prev) => [
      ...new Set([...prev, ...filteredReports.map((report) => report.id)]),
    ]);
  }

  function buildDecisionUpdate(report, nextStatus, options = {}) {
    const next = normalizeStatus(nextStatus);
    const decisionMade = ["approved", "rejected", "resolved"].includes(next);
    const note = String(options.decisionNote || "").slice(0, 100);

    const update = {
      status: next,
      reviewedAt: serverTimestamp(),
      reviewedBy: adminUid || null,
      updatedAt: serverTimestamp(),
      priorityScore: report.priority?.score || 0,
      priorityLevel: report.priority?.level || "Normal",
      priorityFlags: report.priority?.flags || [],
    };

    if (decisionMade) {
      update.resolvedAt = serverTimestamp();
      update.resolvedBy = adminUid || null;
      update.decisionHistory = arrayUnion({
        status: next,
        note,
        decidedAt: new Date().toISOString(),
        decidedBy: adminUid || null,
      });
    }

const cleanedDecisionNote = String(options.decisionNote || "").slice(0, 100);
const decisionIntel = analyseDecisionNote(cleanedDecisionNote);

if (cleanedDecisionNote) {
  update.decisionNote = cleanedDecisionNote;
  update.decisionNoteUpdatedAt = serverTimestamp();
  update.decisionNoteUpdatedBy = adminUid || null;

  update.decisionTone = decisionIntel.decisionTone;
  update.decisionFlags = decisionIntel.decisionFlags;
  update.decisionNoteLength = decisionIntel.decisionNoteLength;
  update.decisionGuidanceChecked = true;
}

    if (decisionMade && hasProof(report) && !report.proofDeletedAt) {
      update.proofDeleteAfter = addHours(settings.evidenceRetentionHoursAfterDecision || 72);
      update.proofDeleteStatus = "scheduled";
      update.proofDeletedAfterDecision = false;
      update.evidenceRetentionHoursAfterDecision =
        settings.evidenceRetentionHoursAfterDecision || 72;
      update.evidenceHardDeleteDays = settings.evidenceHardDeleteDays || 7;
      update.decisionHistory = arrayUnion({
  status: next,
  note: cleanedDecisionNote || "",
  tone: decisionIntel.decisionTone,
  flags: decisionIntel.decisionFlags,
  decidedAt: new Date().toISOString(),
  decidedBy: adminUid || null,
});
    }

    return update;
  }

  async function updateReportStatus(report, nextStatus, options = {}) {
    try {
      setBusyId(report.id);
      setError("");
      setSuccess("");

      const nextInQueue = getNextReport(report.id);

      await setDoc(
        doc(db, "schools", currentSchoolId, "absenceReports", report.id),
        buildDecisionUpdate(report, nextStatus, options),
        { merge: true }
      );

      setSelectedIds((prev) => prev.filter((id) => id !== report.id));
      setSuccess(`Absence marked as ${statusLabel(nextStatus)}.`);

      await loadData();

      if (autoOpenNext && nextInQueue && nextInQueue.id !== report.id) {
        setSelectedReport(nextInQueue);
      } else {
        setSelectedReport(null);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to update absence report.");
    } finally {
      setBusyId("");
    }
  }

  async function bulkUpdateStatus(nextStatus) {
    if (!selectedReports.length) {
      setError("Select at least one report first.");
      return;
    }

    const confirmed = window.confirm(
      `Mark ${selectedReports.length} report(s) as ${statusLabel(nextStatus)}?`
    );
    if (!confirmed) return;

    try {
      setBulkBusy(true);
      setError("");
      setSuccess("");

      const batch = writeBatch(db);
      selectedReports.forEach((report) => {
        batch.set(
          doc(db, "schools", currentSchoolId, "absenceReports", report.id),
          buildDecisionUpdate(report, nextStatus),
          { merge: true }
        );
      });

      await batch.commit();
      setSuccess(`${selectedReports.length} report(s) marked as ${statusLabel(nextStatus)}.`);
      setSelectedIds([]);
      setSelectedReport(null);
      await loadData();
    } catch (err) {
      console.error(err);
      setError("Bulk update failed.");
    } finally {
      setBulkBusy(false);
    }
  }

  function openFirstReport() {
    const first = queueReports[0] || filteredReports[0] || null;
    if (!first) {
      setError("No absence reports available in this view.");
      return;
    }
    setSelectedReport(first);
  }

async function loadEvidencePreview(report) {
  try {
    setEvidenceModalOpen(true);
    setEvidenceLoading(true);
    setEvidenceError("");
    setEvidenceUrl("");
    setEvidenceFileName(report?.proofName || "Supporting document");

    const proofPath =
      report?.proofPath ||
      report?.filePath ||
      report?.storagePath ||
      report?.evidencePath ||
      report?.proof?.path ||
      report?.evidence?.path ||
      report?.attachment?.path ||
      "";

    if (!proofPath) {
      throw new Error("This report has a file name but no saved Storage path.");
    }


const lockdown = await getSecurityLockdown(currentSchoolId);

if (lockdown?.enabled && lockdown?.freezeEvidence) {
  throw new Error(
    "Evidence access temporarily disabled due to security lockdown."
  );
}

const getAbsenceProofUrl = httpsCallable(
  functions,
  "getAbsenceProofUrl"
);

const res = await getAbsenceProofUrl({
  schoolId: currentSchoolId,
  absenceId: report.id,
  proofPath,
  filePath: proofPath,
  storagePath: proofPath,
  evidencePath: proofPath,
});

    const signedUrl = res?.data?.url;

    if (!signedUrl) {
      throw new Error("No secure document link was returned.");
    }

    setEvidenceUrl(signedUrl);
  } catch (err) {
    console.error("Evidence preview failed:", err);
    setEvidenceError(
      err?.message || "The supporting document could not be opened."
    );
  } finally {
    setEvidenceLoading(false);
  }
}

function handleOpenEvidence(report) {
  const deleteStatus = String(report?.proofDeleteStatus || "").toLowerCase();

  if (report?.proofDeletedAt || deleteStatus === "deleted") {
    setError("This evidence has already been automatically deleted.");
    return;
  }

  if (deleteStatus === "deleting") {
    setError("Automatic evidence deletion is currently in progress.");
    return;
  }

  if (!hasRecentSensitiveActionVerification()) {
    setPendingEvidence(report);
    setShowSensitiveModal(true);
    return;
  }

  loadEvidencePreview(report);
}

  return (
    <div style={styles.page}>
      <section style={styles.hero}>
        <div>
          <p style={styles.eyebrow}>Attendance Control</p>
          <h2 style={styles.heroTitle}>Absence Priority Queue</h2>
          <p style={styles.heroText}>
            Review absence requests with priority flags, evidence checks, bulk decisions,
            and automatic proof retention.
          </p>
        </div>

        <div style={styles.statsRow}>
          <Stat label="Open" value={stats.open} />
          <Stat label="High Priority" value={stats.highPriority} />
          <Stat label="Evidence" value={stats.evidence} />
          <Stat label="Approved" value={stats.approved} />
        </div>
      </section>

      {error ? <div style={styles.error}>{error}</div> : null}
      {success ? <div style={styles.success}>{success}</div> : null}

      {stats.highPriority > 0 ? (
        <section style={styles.alertCard}>
          <div>
            <strong>⚡ High priority reports detected</strong>
            <p style={styles.cardText}>
              These reports may require quicker review based on evidence, wording,
              timing, or repeated submissions.
            </p>
          </div>
          <span style={styles.alertBadge}>{stats.highPriority} urgent</span>
        </section>
      ) : null}

      <section style={styles.reviewCard}>
        <div>
          <strong>Smart review queue</strong>
          <p style={styles.cardText}>{queueReports.length} open report(s) ready for decision.</p>
        </div>

        <div style={styles.reviewActions}>
          <label style={styles.checkLabel}>
            <input
              type="checkbox"
              checked={autoOpenNext}
              onChange={(event) => setAutoOpenNext(event.target.checked)}
            />
            Auto-open next
          </label>
          <button
            type="button"
            style={styles.primaryButton}
            onClick={openFirstReport}
            disabled={!filteredReports.length}
          >
            Start Review
          </button>
        </div>
      </section>

      <section style={styles.policyCard}>
        <strong>Temporary evidence policy</strong>
        <p style={styles.cardText}>
          Supporting documents are reviewed only for the absence decision and are
          automatically deleted after the configured retention window. Only metadata
          remains after deletion.
        </p>
      </section>

      <section style={styles.filterCard}>
        <input
          style={styles.searchInput}
          placeholder="Search student, parent, reason, flag..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <div style={styles.pills}>
          {[
            "open",
            "priority",
            "submitted",
            "reviewing",
            "approved",
            "rejected",
            "evidence",
            "all",
          ].map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => setStatusFilter(filter)}
              style={statusFilter === filter ? styles.pillActive : styles.pill}
            >
              {filter === "priority" ? "High Priority" : niceText(filter)}
            </button>
          ))}
        </div>
      </section>

      {selectedIds.length ? (
        <section style={styles.bulkBar}>
          <strong>{selectedIds.length} selected</strong>
          <div style={styles.bulkActions}>
            <button
              type="button"
              style={styles.reviewButton}
              disabled={bulkBusy}
              onClick={() => bulkUpdateStatus("reviewing")}
            >
              Mark Reviewing
            </button>
            <button
              type="button"
              style={styles.approveButton}
              disabled={bulkBusy}
              onClick={() => bulkUpdateStatus("approved")}
            >
              Bulk Approve
            </button>
            <button
              type="button"
              style={styles.rejectButton}
              disabled={bulkBusy}
              onClick={() => bulkUpdateStatus("rejected")}
            >
              Bulk Reject
            </button>
            <button
              type="button"
              style={styles.softButton}
              disabled={bulkBusy}
              onClick={() => setSelectedIds([])}
            >
              Clear
            </button>
          </div>
        </section>
      ) : null}

      <section style={styles.tableCard}>
        {filteredReports.length ? (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                    />
                  </th>
                  <th style={styles.th}>Student</th>
                  <th style={styles.th}>Priority</th>
                  <th style={styles.th}>Parent</th>
                  <th style={styles.th}>Reason</th>
                  <th style={styles.th}>Evidence</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Retention</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr
                    key={report.id}
                    id={`absence-report-row-${report.id}`}
                    style={
                      String(report.id) === String(focusReportId)
                        ? {
                            outline: "3px solid #2563eb",
                            outlineOffset: "-3px",
                            background: "#eff6ff",
                          }
                        : undefined
                    }
                  >
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(report.id)}
                        onChange={() => toggleSelect(report.id)}
                      />
                    </td>
                    <td style={styles.td}>
                      <strong>{getStudentName(report, studentMap)}</strong>
                      <small style={styles.muted}>
                        {getAbsenceDateLabel(report)}
                      </small>
                      <small style={styles.muted}>
                        {getAbsenceDurationLabel(report)}
                      </small>
                    </td>
                    <td style={styles.td}>
                      <PriorityBadge priority={report.priority} />
                      <div style={styles.flags}>
                        {(report.priority?.flags || []).slice(0, 2).map((flag) => (
                          <span key={flag} style={styles.flagMini}>{flag}</span>
                        ))}
                      </div>
                    </td>
                    <td style={styles.td}>{getParentName(report)}</td>
                    <td style={styles.td}>{niceText(report.reason)}</td>
                    <td style={styles.td}>
                      <span style={hasProof(report) ? styles.evidenceBadge : styles.noEvidenceBadge}>
                        {hasProof(report) ? "Evidence attached" : "No evidence"}
                      </span>
                    </td>
                    <td style={styles.td}><StatusBadge status={report.status} /></td>
                    <td style={styles.td}>{getCountdown(report)}</td>
                    <td style={styles.td}>
                      <div style={styles.rowActions}>

                        {hasProof(report) ? (
  <button
    type="button"
    style={styles.viewButton}
    onClick={() => handleOpenEvidence(report)}
  >
    Evidence
  </button>
) : null}
                        <button
                          type="button"
                          style={styles.viewButton}
                          onClick={() => setSelectedReport(report)}
                        >
                          Review
                        </button>
                        <button
                          type="button"
                          style={styles.approveButton}
                          disabled={busyId === report.id}
                          onClick={() => updateReportStatus(report, "approved")}
                        >
                          ✓
                        </button>
                        <button
                          type="button"
                          style={styles.rejectButton}
                          disabled={busyId === report.id}
                          onClick={() => updateReportStatus(report, "rejected")}
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.empty}>No absence reports found.</div>
        )}
      </section>

      {selectedReport ? (
<AbsenceModal
  report={selectedReport}
  schoolId={currentSchoolId}
  studentName={getStudentName(selectedReport, studentMap)}
  parentName={getParentName(selectedReport)}
  busy={busyId === selectedReport.id}
  onClose={() => setSelectedReport(null)}
  onDecision={updateReportStatus}
  onOpenEvidence={handleOpenEvidence}
/>
      ) : null}

      <EvidencePreviewModal
  open={evidenceModalOpen}
  onClose={() => {
    setEvidenceModalOpen(false);
    setEvidenceUrl("");
    setEvidenceError("");
  }}
  url={evidenceUrl}
  fileName={evidenceFileName}
  loading={evidenceLoading}
  error={evidenceError}
/>

<SensitiveActionModal
  open={showSensitiveModal}
  onClose={() => {
    setShowSensitiveModal(false);
    setPendingEvidence(null);
  }}
  title="Re-authenticate to open evidence"
  description="Sensitive safeguarding evidence requires identity confirmation before access is granted."
  actionLabel="Verify & Open Evidence"
  closeAfterVerified={false}
  onVerified={async () => {
    const report = pendingEvidence;

    if (!report) {
      setShowSensitiveModal(false);
      return;
    }

    // Mount the evidence surface first. Its z-index is above the MFA modal,
    // so the admin never sees the page between the two secure layers.
    setEvidenceModalOpen(true);
    setEvidenceLoading(true);
    setEvidenceError("");
    setEvidenceUrl("");
    setEvidenceFileName(report?.proofName || "Supporting document");

    // Let React paint the evidence loader before removing the MFA modal.
    await new Promise((resolve) => requestAnimationFrame(() => resolve()));

    setShowSensitiveModal(false);
    setPendingEvidence(null);

    await loadEvidencePreview(report);
  }}
/>
    </div>
  );
}

const styles = {
  page: {
    padding: 24,
    display: "grid",
    gap: 18,
    background: "transparent",
    minHeight: "100%",
  },
  hero: {
    background: BRAND_GRADIENT,
    color: "#fff",
    borderRadius: 28,
    padding: 28,
    boxShadow: `0 24px 60px ${BRAND_SHADOW}`,
  },
  eyebrow: {
    margin: 0,
    fontSize: 12,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    fontWeight: 900,
    opacity: 0.8,
  },
  heroTitle: {
    margin: "8px 0 0",
    fontSize: 40,
    fontWeight: 950,
    letterSpacing: "-0.04em",
  },
  heroText: {
    margin: "10px 0 0",
    maxWidth: 760,
    lineHeight: 1.6,
    opacity: 0.94,
  },
  statsRow: {
    marginTop: 22,
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
  },
  stat: {
    borderRadius: 20,
    padding: 16,
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.18)",
    backdropFilter: "blur(12px)",
  },
  statValue: { fontSize: 32, fontWeight: 950, lineHeight: 1 },
  statLabel: { marginTop: 8, fontSize: 12, fontWeight: 850, opacity: 0.84 },
  alertCard: {
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 22,
    padding: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 14px 34px rgba(154,52,18,0.08)",
  },
  alertBadge: {
    background: "#ea580c",
    color: "#fff",
    borderRadius: 999,
    padding: "8px 14px",
    fontWeight: 900,
  },
  reviewCard: {
    background: "#fff",
    borderRadius: 22,
    padding: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #e5ebf7",
    boxShadow: "0 14px 34px rgba(20,54,110,0.08)",
  },
  reviewActions: { display: "flex", gap: 12, alignItems: "center" },
  checkLabel: { display: "flex", gap: 6, alignItems: "center", color: "#475569", fontWeight: 750 },
  policyCard: {
    background: "#fff",
    borderRadius: 22,
    padding: 18,
    border: "1px solid #e5ebf7",
    color: "#334155",
  },
  cardText: { margin: "6px 0 0", color: "#64748b", lineHeight: 1.55 },
  filterCard: {
    background: "#fff",
    borderRadius: 22,
    padding: 18,
    display: "grid",
    gridTemplateColumns: "minmax(260px,1fr) auto",
    gap: 14,
    alignItems: "center",
    border: "1px solid #e5ebf7",
    boxShadow: "0 14px 34px rgba(20,54,110,0.07)",
  },
  searchInput: {
    minHeight: 48,
    borderRadius: 16,
    border: "1px solid #d8e1f2",
    padding: "0 16px",
    fontSize: 15,
    outline: "none",
  },
  pills: { display: "flex", gap: 8, flexWrap: "wrap" },
  pill: {
    border: "1px solid #d8e1f2",
    background: "#fff",
    color: "#334155",
    padding: "10px 14px",
    borderRadius: 999,
    fontWeight: 850,
    cursor: "pointer",
  },
  pillActive: {
    border: "1px solid transparent",
    background: BRAND_GRADIENT,
    color: "#fff",
    padding: "10px 14px",
    borderRadius: 999,
    fontWeight: 850,
    cursor: "pointer",
  },
  bulkBar: {
    background: "#111827",
    color: "#fff",
    borderRadius: 20,
    padding: 16,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  bulkActions: { display: "flex", gap: 8, flexWrap: "wrap" },
  tableCard: {
    background: "#fff",
    borderRadius: 24,
    padding: 18,
    border: "1px solid #e5ebf7",
    boxShadow: "0 20px 48px rgba(20,54,110,0.08)",
  },
  tableWrap: { overflowX: "auto", borderRadius: 18, border: "1px solid #edf2fb" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    padding: 14,
    background: "#f8fbff",
    color: "#64748b",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    fontWeight: 900,
    borderBottom: "1px solid #edf2fb",
  },
  td: { padding: 14, borderBottom: "1px solid #f1f5fb", color: "#14213d", verticalAlign: "top" },
  muted: { display: "block", marginTop: 4, color: "#7180a6", fontSize: 12 },
  flags: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 },
  flagMini: {
    background: BRAND_SOFT,
    color: BRAND_DARK,
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 800,
  },
  priorityBadge: { display: "inline-flex", borderRadius: 999, padding: "7px 11px", fontSize: 12, fontWeight: 900 },
  priorityHigh: { background: "#fee2e2", color: "#b42318" },
  priorityMedium: { background: "#fef3c7", color: "#92400e" },
  priorityLow: { background: "#dcfce7", color: "#166534" },
  evidenceBadge: {
    background: "#edf4ff",
    color: "#1d4ed8",
    borderRadius: 999,
    padding: "7px 11px",
    fontSize: 12,
    fontWeight: 900,
  },
  noEvidenceBadge: {
    background: "#f1f5f9",
    color: "#64748b",
    borderRadius: 999,
    padding: "7px 11px",
    fontSize: 12,
    fontWeight: 900,
  },
  statusBadge: { borderRadius: 999, padding: "7px 11px", fontSize: 12, fontWeight: 900 },
  statusApproved: { background: "#dcfae6", color: "#027a48" },
  statusRejected: { background: "#fee4e2", color: "#b42318" },
  statusReviewing: { background: BRAND_SOFT, color: BRAND_DARK },
  statusSubmitted: { background: "#fef0c7", color: "#b54708" },
  rowActions: { display: "flex", gap: 8, flexWrap: "wrap" },
  primaryButton: {
    border: "none",
    background: BRAND_GRADIENT,
    color: "#fff",
    padding: "12px 16px",
    borderRadius: 14,
    fontWeight: 900,
    cursor: "pointer",
  },
  viewButton: { border: "none", background: BRAND_SOFT, color: BRAND_DARK, padding: "9px 12px", borderRadius: 12, fontWeight: 900, cursor: "pointer" },
  approveButton: { border: "none", background: "#dcfae6", color: "#027a48", padding: "9px 12px", borderRadius: 12, fontWeight: 900, cursor: "pointer" },
  rejectButton: { border: "none", background: "#fee4e2", color: "#b42318", padding: "9px 12px", borderRadius: 12, fontWeight: 900, cursor: "pointer" },
  reviewButton: { border: "none", background: BRAND_SOFT, color: BRAND_DARK, padding: "9px 12px", borderRadius: 12, fontWeight: 900, cursor: "pointer" },
  softButton: { border: "none", background: "#e5e7eb", color: "#374151", padding: "9px 12px", borderRadius: 12, fontWeight: 900, cursor: "pointer" },
  empty: { padding: 24, textAlign: "center", color: "#64748b" },
  error: { background: "#fff1f2", color: "#9f1239", border: "1px solid #fecdd3", padding: 14, borderRadius: 16, fontWeight: 850 },
  success: { background: "#ecfdf5", color: "#166534", border: "1px solid #a7f3d0", padding: 14, borderRadius: 16, fontWeight: 850 },
};
